/**
 * Variant Builder Utilities
 * 
 * Pure functions for variant calculations and validation.
 * No side effects, fully testable.
 */

import type { VariantDraft, VariantDraftValidationResult } from '@/lib/erp/variants';
import { VARIANT_CAPABILITY_SCHEMA, type AbbreviationRules } from '@/lib/erp/variants';

/**
 * Calculate total variant count from draft
 * 
 * Formula: product of all attribute value counts
 * Example: Size [S, M, L] × Color [Red, Blue] = 6 variants
 * 
 * Performance: O(n) where n = number of attributes
 */
export function calculateVariantCount(draft: VariantDraft): number {
  if (draft.mode !== 'item_attribute') {
    // Manufacturer mode: 1 variant per manufacturer/part_no combination
    // For Phase 1, we only support item_attribute
    return 0;
  }

  if (!draft.attributes.length) {
    return 0;
  }

  // Product of all attribute value counts
  return draft.attributes.reduce((total, attr) => {
    if (!attr.values.length) {
      return 0; // If any attribute has no values, total is 0
    }
    return total * attr.values.length;
  }, 1);
}

/**
 * Generate variant code preview
 * 
 * Returns sample variant codes (first 5) based on template code and attributes.
 * This is a best-effort preview - actual codes are generated by ERPNext backend.
 * 
 * Performance: O(n) where n = number of variants (limited to 5)
 */
export function generateVariantPreview(
  draft: VariantDraft,
  templateCode?: string
): string[] {
  if (draft.mode !== 'item_attribute') {
    return [];
  }

  if (!draft.attributes.length || !templateCode) {
    return [];
  }

  // Generate all combinations (cartesian product)
  const combinations = generateCombinations(draft.attributes);
  
  // Take first 5
  const preview = combinations.slice(0, 5);
  
  // Format as variant codes (template-attr1-attr2)
  return preview.map(combo => {
    const parts = combo.map((value) => {
      // Use same abbreviation logic as actual generation (single source of truth)
      return generateAbbreviation(value, draft.mode);
    });
    return `${templateCode}-${parts.join('-')}`;
  });
}

/**
 * Generate all attribute value combinations (cartesian product)
 * 
 * Performance: O(product of all attribute value counts)
 * Limited to 5 for preview purposes
 */
function generateCombinations(attributes: VariantDraft['attributes']): string[][] {
  if (!attributes.length) {
    return [];
  }

  // Start with first attribute's values
  let result: string[][] = attributes[0].values.map(val => [val]);

  // For each remaining attribute, multiply combinations
  for (let i = 1; i < attributes.length; i++) {
    const newResult: string[][] = [];
    for (const combo of result) {
      for (const value of attributes[i].values) {
        newResult.push([...combo, value]);
        // Limit to 5 for preview
        if (newResult.length >= 5) {
          return newResult;
        }
      }
    }
    result = newResult;
  }

  return result;
}

/**
 * Generate deterministic variant key from attribute-value pairs
 * 
 * Creates a stable, collision-safe key that is invariant to attribute order.
 * Format: "Attribute1=Value1|Attribute2=Value2|..."
 * 
 * Performance: O(n) where n = number of attributes
 * 
 * @param attributes - Array of attribute-value pairs
 * @returns Deterministic variant key (e.g., "Size=S|Color=Red")
 */
export function makeVariantKey(attributes: Array<{ attribute: string; value: string }>): string {
  // Sort by attribute name for deterministic ordering (not by value)
  const sorted = [...attributes].sort((a, b) => a.attribute.localeCompare(b.attribute));
  
  // Escape special characters: | and = must be escaped
  const escape = (s: string): string => {
    return s.replace(/\|/g, '\\|').replace(/=/g, '\\=');
  };
  
  // Format: "Attribute1=Value1|Attribute2=Value2|..."
  return sorted
    .map(attr => `${escape(attr.attribute)}=${escape(attr.value)}`)
    .join('|');
}

/**
 * Planned variant row - represents a single variant that will be created
 */
export type PlannedVariantRow = {
  /** Unique ID for React key (deterministic variantKey) */
  id: string;
  /** Attribute-value pairs for this variant */
  attributes: Array<{ attribute: string; displayName?: string; value: string }>;
  /** Attributes as a map for backend payload (attribute name -> value) */
  attributesByName: Record<string, string>;
  /** Approximate code preview (best-effort, actual codes generated by ERPNext) */
  codePreview?: string;
};

/**
 * Generate planned variant rows (capped for performance)
 * 
 * Returns rows representing variants that will be created, up to the specified limit.
 * Used for UI preview table - never generates full cartesian product.
 * 
 * Performance: O(min(limit, total combinations))
 * 
 * @param draft - Variant draft configuration
 * @param options - Generation options
 * @returns Array of planned variant rows
 */
export function generatePlannedVariantRows(
  draft: VariantDraft,
  options: { limit?: number; templateCode?: string } = {}
): PlannedVariantRow[] {
  const { limit = 100, templateCode } = options;

  if (draft.mode !== 'item_attribute') {
    return [];
  }

  if (!draft.attributes.length) {
    return [];
  }

  // Generate combinations incrementally, stopping at limit
  const rows: PlannedVariantRow[] = [];
  
  // Start with first attribute's values
  let currentCombos: Array<{ attributes: Array<{ attribute: string; displayName?: string; value: string }> }> =
    draft.attributes[0].values.map(val => ({
      attributes: [{ attribute: draft.attributes[0].attribute, displayName: draft.attributes[0].displayName, value: val }],
    }));

  // For each remaining attribute, multiply combinations
  for (let i = 1; i < draft.attributes.length; i++) {
    const newCombos: Array<{ attributes: Array<{ attribute: string; displayName?: string; value: string }> }> = [];
    for (const combo of currentCombos) {
      for (const value of draft.attributes[i].values) {
        newCombos.push({
          attributes: [...combo.attributes, { attribute: draft.attributes[i].attribute, displayName: draft.attributes[i].displayName, value }],
        });
        // Stop if we've hit the limit
        if (newCombos.length >= limit) {
          break;
        }
      }
      if (newCombos.length >= limit) {
        break;
      }
    }
    currentCombos = newCombos;
    if (currentCombos.length >= limit) {
      break;
    }
  }

  // Convert to PlannedVariantRow format
  for (let idx = 0; idx < Math.min(currentCombos.length, limit); idx++) {
    const combo = currentCombos[idx];
    
    // Generate deterministic variant key
    const variantKey = makeVariantKey(combo.attributes);
    
    // Build attributesByName map for backend payload
    const attributesByName: Record<string, string> = {};
    combo.attributes.forEach(attr => {
      attributesByName[attr.attribute] = attr.value;
    });
    
    const row: PlannedVariantRow = {
      id: variantKey,
      attributes: combo.attributes,
      attributesByName,
    };

    // Generate approximate code preview if template code provided
    if (templateCode) {
      const parts = combo.attributes.map((attr) => {
        return generateAbbreviation(attr.value, draft.mode);
      });
      row.codePreview = `${templateCode}-${parts.join('-')}`;
    }

    rows.push(row);
  }

  return rows;
}

/**
 * Filter variant draft to only include selected variants
 * 
 * @param draft - Original variant draft
 * @param selectedVariantIds - Set of selected variant IDs
 * @returns Filtered variant draft with only selected attribute value combinations
 */
export function filterVariantDraftBySelection(
  draft: VariantDraft,
  selectedVariantIds: Set<string>
): VariantDraft {
  if (draft.mode !== 'item_attribute') {
    return draft;
  }
  
  if (selectedVariantIds.size === 0) {
    // Return draft with empty values (no variants to create)
    return {
      ...draft,
      attributes: draft.attributes.map(attr => ({ ...attr, values: [] })),
    };
  }

  // Generate all planned rows to match IDs (up to maxTotalVariants)
  const capability = VARIANT_CAPABILITY_SCHEMA[draft.mode];
  const allRows = generatePlannedVariantRows(draft, { limit: capability.maxTotalVariants });
  
  // Get unique attribute-value combinations from selected rows
  const selectedAttributeValues = new Map<string, Set<string>>();
  
  allRows.forEach(row => {
    if (selectedVariantIds.has(row.id)) {
      row.attributes.forEach(attr => {
        if (!selectedAttributeValues.has(attr.attribute)) {
          selectedAttributeValues.set(attr.attribute, new Set());
        }
        selectedAttributeValues.get(attr.attribute)!.add(attr.value);
      });
    }
  });

  // Build filtered draft
  const filteredDraft: VariantDraft = {
    ...draft,
    attributes: draft.attributes.map(attr => {
      const selectedValues = selectedAttributeValues.get(attr.attribute);
      if (selectedValues) {
        // Only include values that are in selected variants
        return {
          ...attr,
          values: attr.values.filter(v => selectedValues.has(v)),
        };
      }
      // If no variants selected for this attribute, return empty
      return {
        ...attr,
        values: [],
      };
    }),
  };

  return filteredDraft;
}

/**
 * Map variant draft to ERPNext args format for enqueue_multiple_variant_creation
 * 
 * Converts VariantDraft to the format expected by ERPNext:
 * { "Attribute Name": ["value1", "value2", ...], ... }
 * 
 * @param draft - Variant draft configuration
 * @returns ERPNext args dictionary
 */
export function variantDraftToERPNextArgs(
  draft: VariantDraft
): Record<string, string[]> {
  if (draft.mode !== 'item_attribute') {
    return {};
  }

  const args: Record<string, string[]> = {};

  for (const attr of draft.attributes) {
    // Filter out empty values and trim
    const values = attr.values
      .map(v => v.trim())
      .filter(v => v.length > 0);
    
    if (values.length > 0) {
      args[attr.attribute] = values;
    }
  }

  return args;
}

/**
 * Validate variant draft
 * 
 * Checks:
 * - At least one attribute selected
 * - Each selected attribute has at least one value
 * - Total variant count doesn't exceed max
 * 
 * Performance: O(n) where n = number of attributes
 */
export function validateVariantDraft(draft: VariantDraft): VariantDraftValidationResult {
  const errors: string[] = [];
  const capability = VARIANT_CAPABILITY_SCHEMA[draft.mode];

  if (draft.mode === 'item_attribute') {
    // Check at least one attribute selected
    if (!draft.attributes.length) {
      errors.push('Please select at least one attribute');
    }

    // Check each attribute has at least one value
    for (const attr of draft.attributes) {
      if (!attr.values.length) {
        errors.push(`Attribute "${attr.attribute}" must have at least one value selected`);
      }
    }

    // Check variant count doesn't exceed max
    const count = calculateVariantCount(draft);
    if (count > capability.maxTotalVariants) {
      errors.push(
        `Too many variants (${count}). Maximum allowed is ${capability.maxTotalVariants}`
      );
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

// Note: variantDraftToItemAttributesRows has been moved to @/lib/erp/variants/utils.ts
// Import it from there instead

/**
 * Generate abbreviation from attribute value using schema-defined rules.
 * 
 * SINGLE SOURCE OF TRUTH: Rules are defined in VARIANT_CAPABILITY_SCHEMA.
 * All abbreviation generation (preview, actual save) MUST use this function.
 * 
 * @param attributeValue - The attribute value to generate abbreviation from
 * @param mode - Variant mode (defaults to 'item_attribute' for Phase 1)
 * @returns Non-empty uppercase abbreviation (max length per schema rules)
 */
export function generateAbbreviation(
  attributeValue: string,
  mode: VariantDraft['mode'] = 'item_attribute'
): string {
  const capability = VARIANT_CAPABILITY_SCHEMA[mode];
  const rules = capability.abbreviationRules;
  
  return generateAbbreviationWithRules(attributeValue, rules);
}

/**
 * Generate abbreviation from attribute value using provided rules.
 * 
 * Internal implementation that uses rules from schema.
 * 
 * @param attributeValue - The attribute value to generate abbreviation from
 * @param rules - Abbreviation generation rules from schema
 * @returns Non-empty uppercase abbreviation (max length per rules)
 */
function generateAbbreviationWithRules(
  attributeValue: string,
  rules: AbbreviationRules
): string {
  const trimmed = attributeValue.trim();
  
  // Ultimate fallback: if input is empty/whitespace, return fallback
  if (!trimmed) {
    return rules.fallback;
  }
  
  let result: string;
  
  // If already short (≤ shortThreshold chars), uppercase as-is
  if (trimmed.length <= rules.shortThreshold) {
    result = trimmed.toUpperCase();
  } else {
    // Multi-word: first letter of each word
    const words = trimmed.split(/\s+/).filter(w => w.length > 0);
    if (words.length > 1) {
      if (rules.multiWordStrategy === 'first_letter_each') {
        result = words
          .map((w) => w.charAt(0).toUpperCase())
          .join("");
      } else {
        // Fallback to first_letter_each if unknown strategy
        result = words
          .map((w) => w.charAt(0).toUpperCase())
          .join("");
      }
    } else {
      // Single word: use long strategy
      if (rules.singleWordLongStrategy === 'first_n_chars') {
        result = trimmed.substring(0, rules.longCharCount).toUpperCase();
      } else {
        // Fallback to first_n_chars if unknown strategy
        result = trimmed.substring(0, rules.longCharCount).toUpperCase();
      }
    }
  }
  
  // Enforce max length and ensure non-empty
  result = result.substring(0, rules.maxLength);
  return result || rules.fallback;
}
